import React, { useState } from "react";
import {
  Grid,
  Typography,
  Box,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SaveButton from "components/buttonsComponent/SaveButton";
import { FaUpload } from "react-icons/fa";
import UserIdButton from "components/buttonsComponent/UserIdButton";

// Default evidence types for environmental compliance
const DEFAULT_EVIDENCE_TYPES = [
  "Baseline Environmental Surveys (air, water, soil, biodiversity)",
  "Monitoring Logs and Sampling Results",
  "Satellite Imagery / GIS Layers (KMZ, GeoJSON, PNG)",
  "Emission Lab Results",
  "Permits, Licenses, EIA NOCs",
  "Spill or Incident Logs",
  "NGO or Regulator Comments",
  "Third-party EIA Reports",
  "Ambient Noise/Water Quality Measurement Reports",
  "Waste Manifest and Disposal Records",
  "Water Balance Sheets",
  "Relevant Forms",
  "Self-generated data/forms/questionnaires/surveys/reports",
  "Evidences",
];

const UploadEvidenceComponent = ({
  onPrevious,
  onNext,
  evidenceTypes = [], // Changed default to empty array
  acceptedFileTypes = "image/png,image/jpeg,image/gif,.pdf,.doc,.docx,.xls,.xlsx,.kmz,.geojson",
}) => {
  const [selectedFile, setSelectedFile] = useState(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleSubmit = () => {
    console.log("Submitting form with file:", selectedFile);
    onNext && onNext();
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box
          className="standards-header"
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            [(theme) => theme.breakpoints.down("sm")]: {
              justifyContent: "center",
              flexDirection: "column",
              alignItems: "stretch",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              alignItems: "center",
            }}
          >
            <SaveButton>Save</SaveButton>
            <UserIdButton />
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box
          className="my-4"
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "start",
            gap: 3,
            flexWrap: "wrap",
            [(theme) => theme.breakpoints.down("md")]: {
              flexDirection: "column",
              alignItems: "center",
            },
          }}
        >
          {/* Upload Section */}
          <Paper
            elevation={0}
            sx={{
              top: "27px",
              left: "218px",
              width: 268,
              height: 311,
              p: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              border: "1px dashed #e0e0e0",
              borderRadius: "13px",
              backgroundColor: "#fff",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                  borderRadius: "50%",
                }}
              >
                <FaUpload
                  style={{
                    fontSize: 40,
                    color: "#4b8048",
                    width: "46px",
                    height: "55px",
                  }}
                />
              </Box>

              <Typography
                variant="h6"
                component="h2"
                style={{
                  fontWeight: "bold",
                  mb: 1,
                  color: "#02462E",
                  fontFamily: "DM Sans",
                  fontSize: "20px",
                  lineHeight: "32px",
                }}
              >
                Upload Evidence
              </Typography>

              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 3,
                  fontFamily: "DM Sans",
                  fontWeight: 400,
                  color: "#76C0DB",
                  textAlign: "center",
                }}
              >
                <Typography variant="body2">
                  Attach reports, audit, and other compliance documents
                </Typography>
              </Box>
            </Box>

            <input
              accept={acceptedFileTypes}
              style={{ display: "none" }}
              id="upload-file-button"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="upload-file-button">
              <Button
                variant="outlined"
                component="span"
                sx={{
                  borderColor: "#e0e0e0",
                  color: "#555",
                  "&:hover": {
                    borderColor: "#4b8048",
                    backgroundColor: "rgba(75, 128, 72, 0.04)",
                  },
                  mb: 3,
                }}
              >
                Browse Files
              </Button>
            </label>

            {selectedFile && (
              <Typography
                variant="body2"
                sx={{ textAlign: "center", color: "#4b8048" }}
              >
                Selected: {selectedFile.name}
              </Typography>
            )}
          </Paper>

          {/* Submit Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              maxWidth: "200px",
              gap: 2,
              my: "auto",
            }}
          >
            <Typography
              variant="h5"
              component="h1"
              sx={{
                fontWeight: "bold",
                fontFamily: "DM Sans",
                fontSize: "24px",
                lineHeight: "42px",
                color: "#02462E",
                textAlign: "center",
              }}
            >
              Upload Filled customized form
            </Typography>
            <SaveButton
              onClick={handleSubmit}
              name="Submit form and next"
              icon={null}
              style={{
                backgroundColor: "#02462E",
                "&:hover": { backgroundColor: "#023d28" },
                px: 3,
                py: 1,
                textTransform: "none",
                width: "100%",
              }}
            />
          </Box>
        </Box>
      </Grid>

      {/* Evidence Types List - Conditionally Rendered */}
      {evidenceTypes && evidenceTypes.length > 0 && (
        <Grid item xs={12}>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                maxWidth: 800,
                p: 3,
                border: "1px solid #e0e0e0",
                borderRadius: "13px",
                backgroundColor: "#fff",
                maxHeight: 300,
                overflowY: "auto",
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#888",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "#555",
                },
              }}
            >
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  fontWeight: "bold",
                  fontFamily: "DM Sans",
                  fontSize: "18px",
                  color: "#02462E",
                  mb: 2,
                  textAlign: "center",
                }}
              >
                Upload any of the following as per report selected:
              </Typography>

              <List dense sx={{ py: 0 }}>
                {evidenceTypes.map((evidenceType, index) => (
                  <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircleIcon sx={{ color: "#4b8048", fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={evidenceType}
                      primaryTypographyProps={{
                        fontSize: "14px",
                        fontFamily: "DM Sans",
                        color: "#333",
                        lineHeight: 1.4,
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default UploadEvidenceComponent;